import {useCallback, useEffect, useState} from 'react';
import {Dropdown, Input, Tooltip} from 'antd';
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  LinkOutlined,
  PushpinFilled,
  PushpinOutlined,
  StarOutlined,
} from '@ant-design/icons';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import SubmitButton from 'components/Startup/components/SubmitButton';
import theme from '../../ordersTheme.module.scss';

/**
 * Named filter combinations for the orders queue.
 *
 * Rebuilding "BRVM market orders that are unfilled and over floated" through a
 * 28-field panel every morning is the single most repeated action on this
 * screen. A view stores the filter values verbatim and re-applies them through
 * the page's own apply path, so the request is built by exactly the same code
 * as a manual apply — this adds no API and no new query shape.
 *
 * Stored per admin and per tab in localStorage: a view saved on Open Orders
 * would mean something different on Past Orders, since the tab decides the
 * status and openOrders the request carries.
 */

const keyFor = (userId, activeTab) =>
  `daba.orders.savedViews.${userId || 'anon'}.${activeTab}`;

const read = storageKey => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // A private window, cleared site data, or a browser blocking storage.
    return [];
  }
};

const write = (storageKey, views) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(views));
    return true;
  } catch {
    return false;
  }
};

/**
 * A view in a link.
 *
 * Views live in this browser's storage, so the only way to hand one to a
 * colleague was to describe it field by field. Encoding it into the URL keeps
 * that a copy-paste rather than a backend.
 */
const encodeView = view => {
  try {
    return window.btoa(
      encodeURIComponent(
        JSON.stringify({
          name: view.name,
          filterValues: view.filterValues,
          searchedValue: view.searchedValue,
          user: view.user,
        }),
      ),
    );
  } catch {
    return null;
  }
};

export const decodeView = encoded => {
  try {
    const parsed = JSON.parse(decodeURIComponent(window.atob(encoded)));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    // A truncated or hand-edited link. Nothing to restore.
    return null;
  }
};

/** The view pinned to open a tab with, if the admin pinned one. */
export const readDefaultView = (adminId, activeTab) =>
  read(keyFor(adminId, activeTab)).find(view => view.isDefault) ?? null;

export const SavedViews = ({
  activeTab,
  filterValues,
  searchedValue,
  user,
  hasFilters,
  onApply,
}) => {
  const adminId = useSelector(state => state?.auth?.userData?.user?.id);
  const storageKey = keyFor(adminId, activeTab);

  const [views, setViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  // Set while renaming, so the same modal serves save and rename.
  const [renaming, setRenaming] = useState(null);

  useEffect(() => {
    setViews(read(storageKey));
  }, [storageKey]);

  const persist = useCallback(
    next => {
      setViews(next);
      if (!write(storageKey, next)) {
        toast.error('This browser would not let us save the view');
      }
    },
    [storageKey],
  );

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    if (renaming) {
      persist(
        views.map(view =>
          view.id === renaming.id ? {...view, name: trimmed} : view,
        ),
      );
      toast.success(`Renamed to "${trimmed}"`);
    } else {
      const view = {
        id: `${Date.now()}`,
        name: trimmed,
        filterValues,
        searchedValue,
        user,
      };
      // Saving over an existing name replaces it rather than leaving two views
      // that look identical in the menu.
      const replaced = views.some(v => v.name === trimmed);
      persist([...views.filter(v => v.name !== trimmed), view]);
      toast.success(replaced ? `Updated "${trimmed}"` : `Saved "${trimmed}"`);
    }

    setName('');
    setRenaming(null);
    setSaveOpen(false);
  };

  const handleDelete = (event, id) => {
    event.stopPropagation();
    persist(views.filter(v => v.id !== id));
  };

  const handleRename = (event, view) => {
    event.stopPropagation();
    setRenaming(view);
    setName(view.name);
    setSaveOpen(true);
  };

  /** At most one default per tab — it is the view this queue opens with. */
  const handleDefault = (event, id) => {
    event.stopPropagation();
    persist(
      views.map(view => ({
        ...view,
        isDefault: view.id === id ? !view.isDefault : false,
      })),
    );
  };

  const handleShare = (event, view) => {
    event.stopPropagation();
    const encoded = encodeView(view);
    if (!encoded)
      return toast.error('This view could not be turned into a link');
    const url = `${window.location.origin}${window.location.pathname}?route=${activeTab}&orderView=${encoded}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() =>
          toast.success(
            'Link copied — it carries the filters, not the results',
          ),
        )
        .catch(() => toast.error('Copying the link failed'));
    } else {
      toast.error('This browser would not let us copy the link');
    }
  };

  const items = [
    ...views.map(view => ({
      key: view.id,
      label: (
        <div className={theme.viewItem}>
          <span className={theme.viewName}>{view.name}</span>
          <Tooltip
            title={
              view.isDefault
                ? 'Stop opening with this'
                : 'Open this tab with this view'
            }>
            {view.isDefault ? (
              <PushpinFilled
                className={theme.viewDelete}
                onClick={event => handleDefault(event, view.id)}
              />
            ) : (
              <PushpinOutlined
                className={theme.viewDelete}
                onClick={event => handleDefault(event, view.id)}
              />
            )}
          </Tooltip>
          <Tooltip title="Copy a link to this view">
            <LinkOutlined
              className={theme.viewDelete}
              onClick={event => handleShare(event, view)}
            />
          </Tooltip>
          <Tooltip title="Rename view">
            <EditOutlined
              className={theme.viewDelete}
              onClick={event => handleRename(event, view)}
            />
          </Tooltip>
          <Tooltip title="Delete view">
            <DeleteOutlined
              className={theme.viewDelete}
              onClick={event => handleDelete(event, view.id)}
            />
          </Tooltip>
        </div>
      ),
      onClick: () => onApply(view),
    })),
    ...(views.length ? [{type: 'divider', key: 'divider'}] : []),
    {
      key: 'save',
      label: 'Save current filters…',
      disabled: !hasFilters,
      onClick: () => setSaveOpen(true),
    },
  ];

  return (
    <>
      <Dropdown
        trigger={['click']}
        overlayClassName={theme.menu}
        menu={{items}}>
        <button type="button" className={theme.control}>
          <StarOutlined />
          Views
          {views.length > 0 && (
            <span className={theme.controlCount}>{views.length}</span>
          )}
          <DownOutlined style={{fontSize: 10}} />
        </button>
      </Dropdown>

      <DBModal
        isOpen={saveOpen}
        handleClose={() => {
          setSaveOpen(false);
          setRenaming(null);
          setName('');
        }}
        width={440}
        content={
          <div className={theme.saveView}>
            <h2>{renaming ? 'Rename this view' : 'Save this view'}</h2>
            <p>
              {renaming
                ? 'Only the name changes — the filters it stores stay as they are.'
                : `The filters applied right now are stored on this browser for the ${
                    activeTab === 'ACTIVE' ? 'Open Orders' : 'current'
                  } tab. Reusing a name replaces that view.`}
            </p>
            <Input
              value={name}
              autoFocus
              maxLength={40}
              placeholder="e.g. BRVM unfilled market orders"
              onChange={event => setName(event.target.value)}
              onPressEnter={handleSave}
            />
            <SubmitButton
              label={renaming ? 'Rename view' : 'Save view'}
              type="button"
              disabled={!name.trim()}
              handleClick={handleSave}
            />
          </div>
        }
      />
    </>
  );
};
